import React, { useState } from 'react';
import { User, Membership } from '../types';
import { useLanguage, Language } from '../i18n/LanguageContext';
import { useInstall } from './InstallProvider';

interface HeaderProps {
  user: User | null;
  navigate: (view: string) => void;
  login: (membership: Membership) => void;
}

const NavLink: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} className="text-stone-600 hover:text-accent transition-colors duration-200 py-2">
    {children}
  </button>
);

const LanguageSwitcher: React.FC = () => {
    const { language, setLanguage } = useLanguage();

    const switchLang = (lang: Language) => {
        setLanguage(lang);
    };

    return (
        <div className="flex items-center space-x-1 bg-stone-100 rounded-full p-1">
            <button 
                onClick={() => switchLang('no')}
                className={`px-3 py-1 text-sm font-semibold rounded-full ${language === 'no' ? 'bg-white shadow' : 'text-stone-500'}`}
            >
                NO
            </button>
            <button 
                onClick={() => switchLang('en')}
                className={`px-3 py-1 text-sm font-semibold rounded-full ${language === 'en' ? 'bg-white shadow' : 'text-stone-500'}`}
            >
                EN
            </button>
        </div>
    );
};

const Header: React.FC<HeaderProps> = ({ user, navigate, login }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const { t } = useLanguage();
  const { canInstall, promptInstall } = useInstall();

  const handleNavigate = (view: string) => {
    navigate(view);
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white/30 backdrop-blur-lg sticky top-0 z-50 shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div
            className="text-2xl font-bold text-accent cursor-pointer"
            onClick={() => handleNavigate('home')}
          >
            Breathe to Become
          </div>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-6">
            <NavLink onClick={() => handleNavigate('modules')}>{t('header.modules')}</NavLink>
            <NavLink onClick={() => handleNavigate('audio')}>{t('header.audio')}</NavLink>
            <NavLink onClick={() => handleNavigate('books')}>{t('header.books')}</NavLink>
            <NavLink onClick={() => handleNavigate('the-reset')}>{t('header.theReset')}</NavLink>
            <NavLink onClick={() => handleNavigate('protocols')}>{t('header.protocols')}</NavLink>
            {canInstall && (
              <NavLink onClick={() => promptInstall()}>{t('install.menu')}</NavLink>
            )}
          </nav>

          <div className="flex items-center space-x-4">
             <LanguageSwitcher />
             {user ? (
              <div className="flex items-center space-x-4">
                 <button onClick={() => handleNavigate('notes')} className="hidden md:block text-stone-600 hover:text-accent transition-colors duration-200">
                    {t('header.myNotes')}
                 </button>
                 <button onClick={() => handleNavigate('profile')} className="w-10 h-10 bg-accent rounded-full text-white font-semibold flex items-center justify-center">
                    {user.name.charAt(0)}
                </button>
              </div>
            ) : (
                <button 
                  onClick={() => setIsLoginOpen(true)}
                  className="hidden md:inline-flex items-center justify-center px-5 py-2.5 text-base font-medium text-center text-white bg-accent rounded-full hover:bg-accent-dark focus:ring-4 focus:ring-accent/50"
                >
                  {t('header.login')}
                </button>
            )}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-stone-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}></path></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/50 backdrop-blur-lg">
          <nav className="flex flex-col items-center space-y-2 px-4 pt-2 pb-4">
            <NavLink onClick={() => handleNavigate('modules')}>{t('header.modules')}</NavLink>
            <NavLink onClick={() => handleNavigate('audio')}>{t('header.audio')}</NavLink>
            <NavLink onClick={() => handleNavigate('books')}>{t('header.books')}</NavLink>
            <NavLink onClick={() => handleNavigate('the-reset')}>{t('header.theReset')}</NavLink>
            <NavLink onClick={() => handleNavigate('protocols')}>{t('header.protocols')}</NavLink>
            {canInstall && (
              <NavLink onClick={() => { promptInstall(); setIsMenuOpen(false); }}>{t('install.menu')}</NavLink>
            )}
            {user ? (
                <>
                <NavLink onClick={() => handleNavigate('notes')}>{t('header.myNotes')}</NavLink>
                <NavLink onClick={() => handleNavigate('profile')}>{t('header.profile')}</NavLink>
                </>
            ) : (
                <button
                    onClick={() => { setIsLoginOpen(true); setIsMenuOpen(false); }}
                    className="w-full mt-2 inline-flex items-center justify-center px-5 py-2.5 text-base font-medium text-center text-white bg-accent rounded-full hover:bg-accent-dark focus:ring-4 focus:ring-accent/50"
                >
                    {t('header.login')}
                </button>
            )}
          </nav>
        </div>
      )}

      {/* Login Modal */}
      {isLoginOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setIsLoginOpen(false)}>
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4">{t('header.welcome')}</h2>
                <p className="text-stone-600 mb-6">{t('header.loginPrompt')}</p>
                <div className="space-y-4">
                    <button onClick={() => { login(Membership.Free); setIsLoginOpen(false); }} className="w-full bg-stone-200 text-stone-800 font-semibold py-3 rounded-full hover:bg-stone-300 transition-colors">
                        {t('header.loginFree')}
                    </button>
                    <button onClick={() => { login(Membership.Premium); setIsLoginOpen(false); }} className="w-full bg-accent text-white font-semibold py-3 rounded-full hover:bg-accent-dark transition-colors">
                        {t('header.loginPremium')}
                    </button>
                </div>
            </div>
        </div>
      )}
    </header>
  );
};

export default Header;